import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-emerald-300 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-emerald-400" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-emerald-200">Last updated: January 30, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Africa Market for Trade ("we," "our," or "us") operates LedgerAMT, a document management and invoicing platform. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-4 text-gray-600">
              <div>
                <h3 className="font-semibold text-gray-800">Personal Information</h3>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Name and email address</li>
                  <li>Business name and address</li>
                  <li>Phone numbers</li>
                  <li>Payment information (processed securely by Stripe)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Business Data</h3>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Client information you enter</li>
                  <li>Invoices, quotes, and receipts you create</li>
                  <li>Financial records and transaction data</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Automatically Collected Information</h3>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Device and browser information</li>
                  <li>IP address and location data</li>
                  <li>Usage patterns and preferences</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>To provide and maintain our service</li>
              <li>To process your transactions and send invoices</li>
              <li>To communicate with you about your account</li>
              <li>To improve our services and develop new features</li>
              <li>To prevent fraud and ensure security</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">We do not sell your personal information. We may share data with:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Service Providers:</strong> Companies that help us operate our business (payment processing, email delivery)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your data against unauthorized 
              access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security audits.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your data for as long as your account is active or as needed to provide services. 
              You may request deletion of your data at any time, subject to legal retention requirements.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Access and receive a copy of your data</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and associated data</li>
              <li>Object to certain processing activities</li>
              <li>Withdraw consent where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. International Data Transfers</h2>
            <p className="text-gray-600 leading-relaxed">
              Your data may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place for such transfers in compliance with applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
            <div className="text-gray-600 space-y-2">
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="font-semibold text-gray-800">Africa Market for Trade</p>
                <p>Email: hello@africamarket.co</p>
                <p>Email: hello@ledgeramt.com</p>
                <p>Phone: +1 929-355-2649 | +231777527159</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium">Liberia Office</p>
                    <p>P.O. Box 283 Omega</p>
                    <p>Wehnzon Community</p>
                    <p>Paynesville, Liberia</p>
                  </div>
                  <div>
                    <p className="font-medium">United States Office</p>
                    <p>11 Municipal Drive</p>
                    <p>200PMB1027</p>
                    <p>Fishers, Indiana 46038, USA</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-emerald-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-emerald-200 text-sm">
            © {new Date().getFullYear()} Africa Market for Trade. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
